import Divider from 'core-system/Divider'
import AlertIcon from 'core-system/Icons/Misc/Alert'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { ReactComponent as SuccessIcon } from 'features/Programs/Success/Components/success-icon.svg'
import React from 'react'
import styled from 'styled-components'
import { ComplianceOverviewSummary } from 'redux/compliance/complianceTypes'

const Card = styled.div`
  ${(props) => props.theme.baseCard}
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const MetricValue = styled(Text)<{ $emphasis?: 'success' }>`
  color: ${(props) =>
    props.$emphasis === 'success'
      ? props.theme.palette.secondary.purple600
      : props.theme.palette.text.primary};
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`

const Pills = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const StatusPill = styled.div<{ $variant: 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 999px;
  padding: 0.375rem 0.75rem 0.375rem 0.5rem;
  background-color: ${(props) =>
    props.$variant === 'success'
      ? props.theme.palette.secondary.purple100
      : props.theme.palette.error.red100};
  color: ${(props) =>
    props.$variant === 'success'
      ? props.theme.palette.secondary.purple700
      : props.theme.palette.error.red700};
  ${(props) => props.theme.typography.caption};
  font-weight: 600;
`

const PurpleSuccessIcon = styled(SuccessIcon)`
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: ${palette.secondary.purple600};
`

const ActionNeededIcon = styled(AlertIcon)`
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: ${palette.error.red600};
`

const RateValue = styled.span`
  ${(props) => props.theme.typography.action2};
`

interface ComplianceStatsCardProps {
  stats: ComplianceOverviewSummary
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const ComplianceStatsCard = React.memo((props: ComplianceStatsCardProps) => {
  const { stats } = props

  return (
    <Card>
      <Text variant='h4'>Compliance Overview</Text>
      <MetricsRow>
        <Metric>
          <MetricValue variant='h2'>{formatCount(stats.regions)}</MetricValue>
          <Text variant='body1' color={palette.text.secondary}>
            Regions
          </Text>
        </Metric>
        <Metric>
          <MetricValue variant='h2'>{formatCount(stats.totalSites)}</MetricValue>
          <Text variant='body1' color={palette.text.secondary}>
            Total Sites
          </Text>
        </Metric>
        <Metric>
          <MetricValue variant='h2' $emphasis='success'>
            {formatCount(stats.compliantSites)}
          </MetricValue>
          <Text variant='body1' color={palette.text.secondary}>
            Compliant Sites
          </Text>
        </Metric>
        <Metric>
          <MetricValue variant='h2'>
            {formatCount(stats.totalEmployees)}
          </MetricValue>
          <Text variant='body1' color={palette.text.secondary}>
            Total Employees
          </Text>
        </Metric>
      </MetricsRow>
      <Divider />
      <FooterRow>
        <Text variant='body1'>
          Overall Compliance Rate:{' '}
          <RateValue>{stats.overallComplianceRate}%</RateValue>
        </Text>
        <Pills>
          <StatusPill $variant='success'>
            <PurpleSuccessIcon />
            {formatCount(stats.compliantSites)} Compliant
          </StatusPill>
          <StatusPill $variant='danger'>
            <ActionNeededIcon />
            {formatCount(stats.actionNeeded)} Action Needed
          </StatusPill>
        </Pills>
      </FooterRow>
    </Card>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ComplianceStatsCard.displayName = 'ComplianceStatsCard'
}

export default ComplianceStatsCard
